/* eslint-disable @typescript-eslint/no-empty-function */
import { Table } from '@tanstack/react-table';
import { DataTableFacetedFilter } from 'components/blocks/data-table/data-table-faceted-filter';
import { DateRangeFilter } from 'components/blocks/data-table/data-table-date-filter';
import { mfaEnabled, statuses } from './iam-table-filter-data';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { IamTableOrganizationFilter } from './iam-table-organization-filter';
import { X } from 'lucide-react';
import { Button } from 'components/ui/button';

/**
 * FilterControls Component
 *
 * A set of filter controls for managing and interacting with table data, such as filtering by date range.
 * It provides controls for filtering by creation date and last login date, along with customization for mobile views.
 *
 * Features:
 * - Date range filters for creation date and last login date
 * - Customizable for mobile and desktop views
 * - Supports passing date ranges via props and notifying parent component on change
 * - Utilizes table instance to access filterable columns and update state accordingly
 *
 * Props:
 * - `table` (Table<TData>): The table instance that holds the data and controls for filtering
 * - `isMobile` (boolean, optional): Flag to indicate whether the view is for mobile (default is `false`)
 * - `dateRangeCreate` (DateRange, optional): The current date range for creation date filtering
 * - `dateRangeLastLogin` (DateRange, optional): The current date range for last login date filtering
 * - `onDateRangeCreateChange` (function, optional): Callback that triggers when the creation date range changes
 * - `onDateRangeLastLoginChange` (function, optional): Callback that triggers when the last login date range changes
 *
 * @param {FilterControlsProps<TData>} props - The component props
 * @returns {JSX.Element} A filter controls section with date range pickers for creation and last login dates
 *
 * @example
 * <FilterControls
 *   table={tableInstance}
 *   isMobile={true}
 *   dateRangeCreate={{ start: new Date(), end: new Date() }}
 *   dateRangeLastLogin={{ start: new Date(), end: new Date() }}
 *   onDateRangeCreateChange={(newRange) => console.log(newRange)}
 *   onDateRangeLastLoginChange={(newRange) => console.log(newRange)}
 * />
 */

interface FilterControlsProps<TData> {
  table: Table<TData>;
  isMobile?: boolean;
  dateRangeCreate?: DateRange;
  dateRangeLastLogin?: DateRange;
  onDateRangeCreateChange?: (date: DateRange | undefined) => void;
  onDateRangeLastLoginChange?: (date: DateRange | undefined) => void;
  organizationIds: string[];
  setOrganizationIds: (value: string[]) => void;
  handleResetFilters?: () => void;
  isFiltered?: string | boolean;
}

export function FilterControls<TData>({
  table,
  isMobile = false,
  dateRangeCreate,
  dateRangeLastLogin,
  onDateRangeCreateChange = () => { },
  onDateRangeLastLoginChange = () => { },
  organizationIds,
  setOrganizationIds,
  handleResetFilters,
  isFiltered,
}: Readonly<FilterControlsProps<TData>>) {
  const { t } = useTranslation();
  const toolbarTriggerClassName =
    'h-8 rounded-lg border border-[#E7E7E7] bg-background px-3 text-primary hover:bg-background dark:border-gray-500 dark:text-foreground dark:[&_svg]:text-foreground';

  const getFilterColumn = (columnId: string) => {
    return table.getAllFlatColumns().find((col) => col.id === columnId);
  };

  const containerClass = isMobile
    ? 'flex flex-col space-y-4'
    : 'flex w-full min-w-0 flex-row flex-nowrap items-center gap-2 overflow-x-auto';


  const activeColumn = getFilterColumn('active');
  const mfaEnabledColumn = getFilterColumn('mfaEnabled');
  const createdDateColumn = getFilterColumn('createdDate');
  const lastLoggedInTimeColumn = getFilterColumn('lastLoggedInTime');
  const organizationColumn = getFilterColumn('organization');

  return (
    <div className={containerClass}>
      {activeColumn && (
        <div className={isMobile ? 'w-full' : 'shrink-0'}>
          <DataTableFacetedFilter
            column={activeColumn}
            title={t('STATUS')}
            options={statuses}
            triggerClassName={toolbarTriggerClassName}
          />
        </div>
      )}

      {/* {organizationColumn && (
        <div className={isMobile ? 'w-full' : undefined}>
          <IamTableOrganizationFilter
            selectedIds={organizationIds}
            onSelectionChange={setOrganizationIds}
            title={t('ORGANIZATION')}
            // options={organizations}
            className='w-full xl:w-auto focus:ring-1 border border-dashed text-primary'
          />
        </div>
      )} */}

      {/* {mfaEnabledColumn && (
        <div className={isMobile ? 'w-full' : undefined}>
          <DataTableFacetedFilter column={mfaEnabledColumn} title={t('MFA')} options={mfaEnabled} />
        </div>
      )} */}

      {createdDateColumn && (
        <div className={isMobile ? 'w-full' : 'shrink-0'}>
          <DateRangeFilter
            column={createdDateColumn}
            title={t('JOINED_ON')}
            date={dateRangeCreate}
            onDateChange={onDateRangeCreateChange}
            triggerClassName={toolbarTriggerClassName}
            forceOutline
          />
        </div>
      )}

      {lastLoggedInTimeColumn && (
        <div className={isMobile ? 'w-full' : 'shrink-0'}>
          <DateRangeFilter
            column={lastLoggedInTimeColumn}
            title={t('LAST_LOGIN')}
            date={dateRangeLastLogin}
            onDateChange={onDateRangeLastLoginChange}
            triggerClassName={toolbarTriggerClassName}
            forceOutline
          />
        </div>
      )}

      {isFiltered && (
        <Button variant="ghost" onClick={handleResetFilters} className="h-8 shrink-0 px-2">
          {t('RESET')}
          <X className="ml-2 h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

export default FilterControls;
